import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Box, Flex } from '@mantine/core'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'

import { AdvisorTransactionsList } from '@/modules/advisors/users/pages/[id]/components/AdvisorTransactionsList/AdvisorTransactionsList'
import { UserInfoHeader } from '@/modules/advisors/users/pages/[id]/components/UserInfoHeader/UserInfoHeader'
import { BudgetHeader } from '@/modules/budget/components/BudgetHeader/BudgetHeader'
import { BudgetSummary } from '@/modules/budget/components/BudgetSummary/BudgetSummary'

import { ShadowBox } from '@/components/@Shared/ShadowBox/ShadowBox'
import { Loader } from '@/components/Loader/Loader'
import { Title } from '@/components/Title/Title'

import { TransactionType } from '@/constants/transactions.constants'

import {
  useAdvisorsServiceAdvisorUsersControllerGetTransactions,
  useAdvisorsServiceAdvisorUsersControllerGetUserById,
} from '@/__generated__/queries'

export function Component() {
  const { id: userId } = useParams<{ id: string }>()
  const numericUserId = userId ? +userId : 0

  const now = useMemo(() => dayjs(), [])
  const [startDate, setStartDate] = useState(now.startOf('month').toISOString())
  const [endDate, setEndDate] = useState(now.endOf('month').toISOString())
  const [transactionType, setTransactionType] = useState(
    TransactionType.SPENDING,
  )

  const { data: user } = useAdvisorsServiceAdvisorUsersControllerGetUserById({
    userId: numericUserId,
  })

  const {
    data: transactions,
    isFetching,
    isLoading,
  } = useAdvisorsServiceAdvisorUsersControllerGetTransactions({
    endDate,
    startDate,
    type: transactionType,
    userId: numericUserId,
  })

  const handleMonthChange = (value: Dayjs) => {
    setStartDate(value.startOf('month').toISOString())
    setEndDate(value.endOf('month').toISOString())
  }

  if (!user) return null

  return (
    <Box>
      <UserInfoHeader user={user} userId={userId ?? ''} />

      <Title mb={16} variant="h1">
        Transactions
      </Title>

      <ShadowBox p={0}>
        <BudgetHeader
          now={now}
          onMonthChange={handleMonthChange}
          onTransactionTypeChange={setTransactionType}
          selectedType={transactionType}
        />

        <Flex
          direction={{
            base: 'column',
            lg: 'row',
          }}
        >
          <BudgetSummary
            endDate={endDate}
            selectedType={transactionType}
            startDate={startDate}
            userId={numericUserId}
          />

          <Box p={24} style={{ flexGrow: 1 }}>
            {isLoading || isFetching ? (
              <Box h={110}>
                <Loader isMinimized />
              </Box>
            ) : (
              <AdvisorTransactionsList
                transactions={transactions ?? null}
                type={transactionType}
              />
            )}
          </Box>
        </Flex>
      </ShadowBox>
    </Box>
  )
}

Component.displayName = 'UserTransactions'
